import { useState } from "react";
import { toast } from "sonner";
import { ReceiptText } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Field from "@/components/Field";
import DocSlot from "@/components/DocSlot";
import { fmtAUD, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

// External Cost amounts are stored/entered Excl. GST — displayed unchanged with the compact grey 'excl' tail.
export const ExclAmt = ({ v, className = "" }) => (
  <span className={`whitespace-nowrap ${className}`}>{fmtAUD(v || 0)}<span className="ml-0.5 align-baseline text-[0.62em] font-semibold text-gray-500">excl</span></span>
);

// v1.0.17 VQR Cost colour model: Pending = orange, Unpaid = red, Paid / Existing Asset = green
export const PENDING_TONE = { cls: "border-orange-200 bg-orange-50 hover:border-orange-300", text: "text-orange-600" };
export const UNPAID_TONE = { cls: "border-red-300 bg-red-50 hover:border-red-400", text: "text-red-700" };
export const PAID_TONE = { cls: "border-emerald-300 bg-emerald-50 hover:border-emerald-400", text: "text-emerald-700" };

export const costPaymentState = (c) => (c && c.payment_state) || (c && c.paid ? "paid" : "unpaid"); // legacy: paid→Paid, else Unpaid
const STATE_TONE = { pending: PENDING_TONE, unpaid: UNPAID_TONE, paid: PAID_TONE };

export function PaymentStateSelector({ value, onChange, disabled, testPrefix }) {
  const opts = [["pending", "Pending", "border-orange-500 bg-orange-100 text-orange-800"], ["unpaid", "Unpaid", "border-red-500 bg-red-100 text-red-800"], ["paid", "Paid", "border-emerald-500 bg-emerald-100 text-emerald-800"]];
  return (
    <div className="flex gap-2" data-testid={testPrefix}>
      {opts.map(([val, label, on]) => (
        <button key={val} type="button" data-testid={`${testPrefix}-${val}`} disabled={disabled} onClick={() => onChange(val)}
          className={`bdx-btn ${value === val ? `${on} font-semibold` : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}>{label}</button>
      ))}
    </div>
  );
}

export function ExternalCostChip({ cost, onClick, testId, subLabel, variant = "cost" }) {
  const { readOnly } = useAuth();
  const c = cost || {};
  const clickable = true; // v1.0.18: BLG Viewer opens the drawer read-only to view / download attachments
  const base = "w-full rounded-md border px-3 py-2 text-left shadow-sm transition-colors";
  const inner = (() => {
    if (variant === "quote") {
      const qps = c.quote_payment_state || "pending";
      const tone = STATE_TONE[qps] || PENDING_TONE;
      const label = qps === "paid" ? "Paid" : qps === "unpaid" ? "Unpaid" : `Pending${c.quote_date ? ` · ${fmtDate(c.quote_date)}` : ""}`;
      return { cls: tone.cls, body: (
        <>
          <div className="font-mono-data text-sm font-semibold text-gray-950"><ExclAmt v={c.quote_amount} /></div>
          <div className={`text-[10px] font-semibold uppercase ${tone.text}`} style={{ letterSpacing: "0.05em" }} data-testid={testId ? `${testId}-state` : undefined}>{label}</div>
        </>
      ) };
    }
    if (c.mode === "existing_asset") {
      return { cls: PAID_TONE.cls, body: (
        <>
          <div className="text-[10px] font-bold uppercase text-emerald-800" style={{ letterSpacing: "0.08em" }}>Existing Asset</div>
          <div className={`text-[10px] font-semibold uppercase ${PAID_TONE.text}`} style={{ letterSpacing: "0.05em" }}>Completed by Others</div>
        </>
      ) };
    }
    if (c.mode === "invoice") {
      const st = costPaymentState(c);
      const tone = STATE_TONE[st];
      const label = st === "paid" ? `Paid${c.paid_date ? ` · ${fmtDate(c.paid_date)}` : ""}`
        : st === "pending" ? `Pending${c.due_date ? ` · due ${fmtDate(c.due_date)}` : ""}`
          : (c.due_date ? `Due · ${fmtDate(c.due_date)}` : "Unpaid");
      return { cls: tone.cls, body: (
        <>
          <div className="font-mono-data text-sm font-semibold text-gray-950"><ExclAmt v={c.amount} /></div>
          <div className={`text-[10px] font-semibold uppercase ${tone.text}`} style={{ letterSpacing: "0.05em" }} data-testid={testId ? `${testId}-state` : undefined}>{label}</div>
        </>
      ) };
    }
    return { cls: "border-dashed border-gray-300 bg-gray-50/80 hover:border-gray-400", body: (
      <div className="text-[10px] font-medium uppercase text-gray-400" style={{ letterSpacing: "0.06em" }}>Not Recorded</div>
    ) };
  })();
  const paidCard = inner.cls === PAID_TONE.cls; // Paid / Existing Asset → dark neutral headings (v1.0.17 Pass 1A)
  return (
    <button type="button" data-testid={testId} data-paid={paidCard ? "true" : "false"} onClick={clickable ? onClick : undefined}
      className={`${base} ${inner.cls} ${clickable ? "cursor-pointer" : "cursor-default"}`}
      title={readOnly ? "View VQR Cost attachments (read-only)" : "Edit VQR Cost (reference only)"}>
      <div className={`mb-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase ${paidCard ? "text-gray-800" : "text-gray-500"}`} style={{ letterSpacing: "0.1em" }}>
        <ReceiptText size={11} strokeWidth={1.8} /> VQR Cost
      </div>
      {subLabel && <div className={`mb-0.5 text-[9px] font-bold uppercase ${paidCard ? "text-gray-950" : "text-orange-700"}`} style={{ letterSpacing: "0.1em" }} data-testid={testId ? `${testId}-sublabel` : undefined}>{subLabel}</div>}
      {inner.body}
    </button>
  );
}

function SectionHeading({ children }) {
  return <div className="mb-2 text-[10px] font-bold uppercase text-orange-700" style={{ letterSpacing: "0.12em" }}>{children}</div>;
}

export function ExternalCostForm({ project, scope, cost, remitLinkedTo, docs, refresh, refreshDocs, onClose }) {
  const { readOnly } = useAuth();
  const c = cost || {};
  const withQuote = scope === "electrical_authority";
  const [form, setForm] = useState({
    mode: c.mode || "",
    amount: c.amount ?? "",
    due_date: c.due_date || "",
    payment_state: c.mode === "invoice" ? costPaymentState(c) : "pending", // new activation defaults to Pending; legacy paid→Paid, else Unpaid
    paid_date: c.paid_date || "",
    quote_required: !!c.quote_required,
    quote_amount: c.quote_amount ?? "",
    quote_date: c.quote_date || "",
    quote_payment_state: c.quote_payment_state || "pending",
  });
  const [busy, setBusy] = useState(false);
  const quoteDocs = (docs || []).filter((d) => d.linked_to === "authority_quote");
  const quoteHasData = !!c.quote_required && (c.quote_amount != null || !!c.quote_date || quoteDocs.length > 0);

  const setQuoteRequired = (val) => {
    if (val === form.quote_required) return;
    if (!val && quoteHasData) {
      if (!window.confirm(`Remove the Assessment Fee?\n\nThe Assessment Fee date, amount, payment state${quoteDocs.length ? `, ${quoteDocs.length} document(s)` : ""} and its row in the VQR Costs Register will be removed when you save. The Application Cost is not affected.`)) return;
    }
    setForm({ ...form, quote_required: val });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/external-cost/${scope}`, {
        mode: form.mode || null,
        amount: form.amount === "" ? null : parseFloat(form.amount),
        due_date: form.due_date || null,
        payment_state: form.payment_state,
        paid: form.payment_state === "paid",
        paid_date: form.paid_date || null,
        ...(withQuote ? {
          quote_required: form.mode === "invoice" && form.quote_required,
          quote_amount: form.quote_amount === "" ? null : parseFloat(form.quote_amount),
          quote_date: form.quote_date || "",
          quote_payment_state: form.quote_payment_state,
        } : {}),
      });
      toast.success("VQR Cost reference saved");
      refresh();
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const invoice = form.mode === "invoice";

  return (
    <div data-testid={`external-cost-form-${scope}`}>
      <p className="mb-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-500">
        Reference only — this VQR Cost sits outside the project contract value and never affects the Base Contract, Variations, Claims, Contract Sum or completion. All amounts Excl. GST.
      </p>
      <Field label="Status">
        <select data-testid={`ext-cost-mode-${scope}`} className="bdx-input" disabled={readOnly} value={form.mode}
          onChange={(e) => setForm({ ...form, mode: e.target.value })}>
          <option value="">Not Recorded</option>
          <option value="existing_asset">Existing Asset — Completed by Others</option>
          <option value="invoice">Authority Invoice / VQR Cost</option>
        </select>
      </Field>

      {invoice && withQuote && (
        <div className="mt-4 rounded-md border border-orange-200 bg-orange-50/40 px-3 py-3" data-testid="ext-quote-section">
          <SectionHeading>Assessment Fee</SectionHeading>
          <div className="bdx-label mb-1.5">Assessment Fee Required?</div>
          <div className="flex gap-2">
            {[["No", false], ["Yes", true]].map(([label, val]) => (
              <button key={label} type="button" data-testid={`ext-quote-required-${label.toLowerCase()}`} disabled={readOnly}
                onClick={() => setQuoteRequired(val)}
                className={`bdx-btn ${form.quote_required === val ? "border-orange-500 bg-orange-100 font-semibold text-orange-800" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}>
                {label}
              </button>
            ))}
          </div>
          {form.quote_required ? (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Assessment Fee Date"><input data-testid="ext-quote-date" type="date" className="bdx-input" disabled={readOnly} value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} /></Field>
                <Field label="Assessment Fee Amount ($ Excl. GST)"><input data-testid="ext-quote-amount" type="number" step="0.01" min="0" className="bdx-input font-mono-data" disabled={readOnly} value={form.quote_amount} onChange={(e) => setForm({ ...form, quote_amount: e.target.value })} /></Field>
                <Field label="Payment State" className="col-span-2">
                  <PaymentStateSelector value={form.quote_payment_state} disabled={readOnly} testPrefix="ext-quote-state" onChange={(v) => setForm({ ...form, quote_payment_state: v })} />
                </Field>
              </div>
              <p className="mt-1 text-[10px] text-gray-400">Independent of the Application Cost — its own payment state drives only the Assessment Fee container and register row, never the Project Register Supply indicator.</p>
              <div className="mt-3">
                <DocSlot projectId={project.id} linkedTo="authority_quote" category="External Cost" label="Assessment Fee / Reference Documents" docs={docs} onUploaded={refreshDocs} />
              </div>
            </>
          ) : (
            <p className="mt-2 text-[10px] text-gray-400">No Assessment Fee — proceeds directly to the Application Cost.</p>
          )}
        </div>
      )}

      {invoice && (
        <div className={`mt-4 ${withQuote ? "rounded-md border border-orange-200 bg-orange-50/40 px-3 py-3" : ""}`} data-testid={`ext-cost-section-${scope}`}>
          {withQuote && <SectionHeading>Application Cost</SectionHeading>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Invoice Amount ($ Excl. GST)"><input data-testid={`ext-cost-amount-${scope}`} type="number" step="0.01" min="0" className="bdx-input font-mono-data" disabled={readOnly} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Due Date (optional)"><input data-testid={`ext-cost-due-${scope}`} type="date" className="bdx-input" disabled={readOnly} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
            <Field label="Payment State" className="col-span-2">
              <PaymentStateSelector value={form.payment_state} disabled={readOnly} testPrefix={`ext-cost-state-${scope}`} onChange={(v) => setForm({ ...form, payment_state: v })} />
            </Field>
            {form.payment_state === "paid" && (
              <Field label="Paid Date (optional)"><input data-testid={`ext-cost-paid-date-${scope}`} type="date" className="bdx-input" disabled={readOnly} value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} /></Field>
            )}
          </div>
          <div className="mt-4">
            <DocSlot projectId={project.id} linkedTo={remitLinkedTo} category="Other" label="Remittance / Receipt Document" docs={docs} onUploaded={refreshDocs} />
          </div>
        </div>
      )}
      {!readOnly && (
        <div className="mt-4 flex justify-end border-t border-gray-100 pt-3">
          <button data-testid={`ext-cost-save-${scope}`} className="bdx-btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      )}
    </div>
  );
}
